import { unwrapResult } from '~/modules/query/base';
import { notificationApi } from '~/modules/query/notification';
import type {
  CreateTemplateRequest,
  PreviewTemplateRequest,
  PreviewTemplateResponse,
  PublishTemplateRequest,
  Template,
  UpdateTemplateRequest,
} from '~/modules/query/notification/types';

const seg = encodeURIComponent;

export const templatesApi = notificationApi.injectEndpoints({
  endpoints: (builder) => ({
    listTemplates: builder.query<Template[], { customerId?: string } | void>({
      query: (args) => ({ url: '/templates', method: 'GET', params: args ?? undefined }),
      transformResponse: unwrapResult<Template[]>,
      providesTags: (result) => [
        'NotificationTemplate',
        ...(result ?? []).map((t) => ({ type: 'NotificationTemplate' as const, id: t.id })),
      ],
    }),
    getTemplate: builder.query<Template, { id: string; version?: number }>({
      query: ({ id, version }) => ({
        url: `/templates/${seg(id)}`,
        method: 'GET',
        params: version === undefined ? undefined : { version },
      }),
      transformResponse: unwrapResult<Template>,
      providesTags: (_result, _error, { id }) => [{ type: 'NotificationTemplate', id }],
    }),
    createTemplate: builder.mutation<Template, CreateTemplateRequest>({
      query: (data) => ({ url: '/templates', method: 'POST', data }),
      transformResponse: unwrapResult<Template>,
      invalidatesTags: ['NotificationTemplate'],
    }),
    updateTemplate: builder.mutation<Template, UpdateTemplateRequest>({
      query: ({ id, ...data }) => ({ url: `/templates/${seg(id)}`, method: 'PUT', data }),
      transformResponse: unwrapResult<Template>,
      invalidatesTags: (_result, _error, { id }) => [{ type: 'NotificationTemplate', id }],
    }),
    publishTemplate: builder.mutation<Template, PublishTemplateRequest>({
      query: ({ id, templateVersion }) => ({
        url: `/templates/${seg(id)}/publish`,
        method: 'POST',
        data: templateVersion === undefined ? {} : { templateVersion },
      }),
      transformResponse: unwrapResult<Template>,
      invalidatesTags: (_result, _error, { id }) => [{ type: 'NotificationTemplate', id }],
    }),
    deactivateTemplate: builder.mutation<{ deleted: true; id: string }, string>({
      query: (id) => ({ url: `/templates/${seg(id)}`, method: 'DELETE' }),
      transformResponse: unwrapResult<{ deleted: true; id: string }>,
      invalidatesTags: (_result, _error, id) => [{ type: 'NotificationTemplate', id }],
    }),
    previewTemplate: builder.mutation<PreviewTemplateResponse, PreviewTemplateRequest>({
      query: (data) => ({ url: '/templates/preview', method: 'POST', data }),
      transformResponse: unwrapResult<PreviewTemplateResponse>,
    }),
  }),
});

export const {
  useListTemplatesQuery,
  useGetTemplateQuery,
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
  usePublishTemplateMutation,
  useDeactivateTemplateMutation,
  usePreviewTemplateMutation,
} = templatesApi;
